// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PhotoUpload from '../components/PhotoUpload';
import AppLayout from '../components/AppLayout';

function MemberFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    birth_date: '',
    gender: '',
    address: '',
    category: 'visitor',
    was_previously_baptized: false,
    has_encounter: false,
    encounter_date: '',
    baptism_date: '',
    completed_ctl: false,
    completed_cme: false,
    completed_stp: false,
    cell_id: '',
    skill_ids: [], // IDs das habilidades selecionadas
    role_ids: [], // IDs dos roles selecionados
    supervisor_id: '', // ID do supervisor
  });

  const [photoFile, setPhotoFile] = useState(null); // Arquivo de foto para upload
  const [photoPreview, setPhotoPreview] = useState(null); // Preview da foto selecionada
  const [errors, setErrors] = useState({});

  // Carrega membro se estiver editando
  const { data: memberData, isLoading: loadingMember } = useQuery({
    queryKey: ['member', id],
    queryFn: async () => {
      const response = await api.get(`/members/${id}`);
      return response.data.data;
    },
    enabled: isEdit,
  });



  // Carrega células para dropdown
  const { data: cellsData } = useQuery({
    queryKey: ['cells'],
    queryFn: async () => {
      const response = await api.get('/cells');
      return response.data.data || [];
    },
  });

  // Carrega roles disponíveis
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await api.get('/roles');
      return response.data.data || [];
    },
  });

  // Carrega habilidades disponíveis
  const { data: skillsData } = useQuery({
    queryKey: ['skills'],
    queryFn: async () => {
      const response = await api.get('/skills');
      return response.data.data || [];
    },
  });

  // Preenche formulário quando membro é carregado
  useEffect(() => {
    if (memberData && isEdit) {
      setFormData({
        full_name: memberData.full_name || '',
        email: memberData.email || '',
        phone: memberData.phone || '',
        birth_date: memberData.birth_date ? memberData.birth_date.split('T')[0] : '',
        gender: memberData.gender || '',
        address: memberData.address || '',
        category: memberData.category || 'visitor',
        was_previously_baptized: memberData.was_previously_baptized || false,
        has_encounter: memberData.has_encounter || false,
        encounter_date: memberData.encounter_date ? memberData.encounter_date.split('T')[0] : '',
        baptism_date: memberData.baptism_date ? memberData.baptism_date.split('T')[0] : '',
        completed_ctl: memberData.completed_ctl || false,
        completed_cme: memberData.completed_cme || false,
        completed_stp: memberData.completed_stp || false,
        cell_id: memberData.cells?.[0]?.id || '',
        skill_ids: memberData.skills?.map(s => s.id) || [],
        role_ids: memberData.roles?.map(r => r.id) || [], // Preenche roles
        supervisor_id: memberData.supervisor_id || '', // Preenche supervisor
      });
    }
  }, [memberData, isEdit]);



  // Mutation para criar/atualizar membro
  const memberMutation = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        return api.put(`/members/${id}`, data);
      } else {
        return api.post('/members', data);
      }
    }
  });

  // Handler de mudança de campo
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Limpa erro do campo ao modificar
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler para seleção de roles
  const handleRoleToggle = (roleId) => {
    setFormData(prev => {
      const roleIds = prev.role_ids || [];
      const newRoleIds = roleIds.includes(roleId)
        ? roleIds.filter(id => id !== roleId)
        : [...roleIds, roleId];
      return {
        ...prev,
        role_ids: newRoleIds,
      };
    });
  };

  // Handler para seleção de habilidades
  const handleSkillToggle = (skillId) => {
    setFormData(prev => {
      const skillIds = prev.skill_ids || [];
      const newSkillIds = skillIds.includes(skillId)
        ? skillIds.filter(id => id !== skillId)
        : [...skillIds, skillId];
      return {
        ...prev,
        skill_ids: newSkillIds,
      };
    });
  };

  // Handler de submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validação básica
    const newErrors = {};
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Nome completo é obrigatório';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepara dados para envio
    const submitData = {
      full_name: formData.full_name.trim(),
      email: formData.email.trim() || null,
      phone: formData.phone.trim() || null,
      birth_date: formData.birth_date || null,
      gender: formData.gender || null,
      address: formData.address.trim() || null,
      category: formData.category,
      was_previously_baptized: formData.was_previously_baptized ? 1 : 0,
      has_encounter: formData.has_encounter ? 1 : 0,
      encounter_date: formData.has_encounter && formData.encounter_date ? formData.encounter_date : null,
      baptism_date: formData.was_previously_baptized && formData.baptism_date ? formData.baptism_date : null,
      completed_ctl: formData.completed_ctl ? 1 : 0,
      completed_cme: formData.completed_cme ? 1 : 0,
      completed_stp: formData.completed_stp ? 1 : 0,
      supervisor_id: formData.supervisor_id || null,
      role_ids: formData.category === 'member' ? formData.role_ids : [],
      skill_ids: formData.category === 'member' ? formData.skill_ids : [],
    };

    // Adiciona cell_id tanto na criação quanto na edição
    if (formData.cell_id) {
      submitData.cell_id = parseInt(formData.cell_id);
    } else {
      submitData.cell_id = null; // Permite remover da célula
    }

    try {
      const response = await memberMutation.mutateAsync(submitData);
      
      const member = response.data.data;
      const memberId = member.id || id;

      // Faz upload da foto se existir
      if (photoFile) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('photo', photoFile);
          await api.post(`/members/${memberId}/photo`, photoFormData);
        } catch (error) {
          console.error('Erro ao fazer upload da foto:', error);
        }
      }

      // Invalida cache
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['members-search'] });
      queryClient.invalidateQueries({ queryKey: ['member', id] });
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });

      // Redireciona
      navigate(`/members/${memberId}`);
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        setErrors(errorData.errors);
      } else {
        setErrors({ general: errorData?.message || error.message || 'Erro ao salvar membro' });
      }
    }
  };

  // Handler de upload de foto (quando membro já existe)
  const handlePhotoUploadSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['member', id] });
  };

  // Categorias disponíveis
  const categories = [
    { value: 'visitor', label: 'Visitante' },
    { value: 'frequent', label: 'Frequentador Assíduo' },
    { value: 'frequent_encounter', label: 'Frequentador + Encontro' },
    { value: 'member', label: 'Membro' },
  ];

  // Gêneros
  const genders = [
    { value: '', label: 'Selecione...' },
    { value: 'male', label: 'Masculino' },
    { value: 'female', label: 'Feminino' },
    { value: 'other', label: 'Outro' },
  ];

  if (isEdit && loadingMember) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="member-form-page" style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            className="btn btn-text"
            onClick={() => navigate('/members')}
            style={{ marginBottom: '1rem' }}
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Voltar
          </button>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
            {isEdit ? 'Editar Membro' : 'Adicionar Membro'}
          </h1>
        </div>

        {/* Erro geral */}
        {errors.general && (
          <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Informações Básicas
            </h2>

            {/* Foto */}
            <div className="form-group" style={{ marginBottom: '2rem' }}>
              <label className="form-label">Foto</label>
              <PhotoUpload
                memberId={isEdit ? id : null}
                currentPhotoUrl={isEdit ? memberData?.photo_url : photoPreview}
                currentThumbnailUrl={isEdit ? memberData?.photo_thumbnail_url : null}
                onFileSelect={isEdit ? null : (file) => {
                  setPhotoFile(file);
                  // Gera preview
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setPhotoPreview(reader.result);
                  };
                  reader.readAsDataURL(file);
                }}
                onUploadSuccess={isEdit ? handlePhotoUploadSuccess : null}
                onUploadError={(error) => {
                  setErrors(prev => ({ ...prev, photo: error }));
                }}
              />
              {errors.photo && (
                <div className="alert alert-error" style={{ marginTop: '0.5rem' }}>
                  {errors.photo}
                </div>
              )}
              {!isEdit && (
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  A foto será salva após clicar em &quot;Criar Membro&quot;
                </p>
              )}
            </div>

            {/* Nome Completo */}
            <div className="form-group">
              <label className="form-label">
                Nome Completo <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                type="text"
                name="full_name"
                className={`form-input ${errors.full_name ? 'error' : ''}`}
                value={formData.full_name}
                onChange={handleChange}
                required
                placeholder="Ex: João Silva"
              />
              {errors.full_name && (
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {errors.full_name}
                </span>
              )}
            </div>

            {/* Email e Telefone */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="exemplo@email.com"
                />
                {errors.email && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Telefone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            {/* Data de Nascimento e Gênero */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Data de Nascimento</label>
                <input
                  type="date"
                  name="birth_date"
                  className="form-input"
                  value={formData.birth_date}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Gênero</label>
                <select
                  name="gender"
                  className="form-input"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  {genders.map(gender => (
                    <option key={gender.value} value={gender.value}>{gender.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Endereço */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Endereço</label>
              <textarea
                name="address"
                className="form-input"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                placeholder="Rua, número, bairro, cidade..."
              />
            </div>
          </div>

          {/* Categoria e Status */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Categoria e Status
            </h2>

            {/* Categoria */}
            <div className="form-group">
              <label className="form-label">Categoria</label>
              <select
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleChange}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                A categoria pode ser alterada automaticamente pelo sistema baseado na frequência e eventos
              </p>
            </div>

            {/* Foi Batizado Anteriormente */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="was_previously_baptized"
                  checked={formData.was_previously_baptized}
                  onChange={handleChange}
                />
                <span>Foi batizado anteriormente</span>
              </label>
            </div>

            {/* Data do Batismo (condicional) */}
            {formData.was_previously_baptized && (
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label className="form-label">Data do Batismo</label>
                <input
                  type="date"
                  name="baptism_date"
                  className="form-input"
                  value={formData.baptism_date}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Encontro com Deus */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="has_encounter"
                  checked={formData.has_encounter}
                  onChange={handleChange}
                />
                <span>⭐ Participou do Encontro com Deus</span>
              </label>
            </div>

            {/* Data do Encontro (condicional) */}
            {formData.has_encounter && (
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label className="form-label">Data do Encontro com Deus</label>
                <input
                  type="date"
                  name="encounter_date"
                  className="form-input"
                  value={formData.encounter_date}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Cursos/Formações */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">🎓 Formações Concluídas</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="completed_ctl"
                    checked={formData.completed_ctl}
                    onChange={handleChange}
                  />
                  <span>CTL - Curso de Treinamento de Líderes</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="completed_cme"
                    checked={formData.completed_cme}
                    onChange={handleChange}
                  />
                  <span>CME - Curso de Maturidade Espiritual</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="completed_stp"
                    checked={formData.completed_stp}
                    onChange={handleChange}
                  />
                  <span>STP - Seminário Teológico Pastoral</span>
                </label>
              </div>
            </div>

            {/* Célula */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Célula (opcional)</label>
              <select
                name="cell_id"
                className="form-input"
                value={formData.cell_id}
                onChange={handleChange}
              >
                <option value="">Selecione uma célula...</option>
                {cellsData?.map(cell => (
                  <option key={cell.id} value={cell.id}>{cell.name}</option>
                ))}
              </select>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                {isEdit
                  ? 'Alterar a célula removerá o membro da célula atual e o adicionará à nova célula selecionada'
                  : 'O membro pode ser adicionado a outras células após o cadastro'}
              </p>
            </div>
          </div>

          {/* Habilidades e Funções (apenas para membros) */}
          {formData.category === 'member' && (
            <>
              {/* Funções / Cargos */}
              <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                  Funções e Cargos
                </h2>



                <label className="form-label">Cargos Eclesiásticos / Executivos</label>
                {rolesData && rolesData.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '0.75rem',
                    marginTop: '0.5rem'
                  }}>
                    {rolesData.map(role => (
                      <label
                        key={role.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem',
                          border: formData.role_ids?.includes(role.id)
                            ? '2px solid var(--primary)'
                            : '1px solid var(--border)',
                          borderRadius: 'var(--radius-lg)',
                          cursor: 'pointer',
                          background: formData.role_ids?.includes(role.id)
                            ? 'var(--primary-light)'
                            : 'var(--surface)',
                          transition: 'all 0.2s ease',
                          opacity: role.is_system ? 0.7 : 1, // Visual cue para roles de sistema
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.role_ids?.includes(role.id) || false}
                          onChange={() => handleRoleToggle(role.id)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', color: 'var(--primary)' }}>
                          badge
                        </span>
                        <span style={{ flex: 1, fontWeight: formData.role_ids?.includes(role.id) ? '500' : '400' }}>
                          {role.name}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '1rem',
                    background: 'var(--surface-hover)',
                    borderRadius: 'var(--radius-lg)',
                    color: 'var(--text-muted)',
                    fontSize: '0.875rem',
                    textAlign: 'center'
                  }}>
                    Nenhuma função disponível. Crie funções na página de Funções.
                  </div>
                )}
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  Selecione os cargos executivos ou eclesiásticos deste membro.
                </p>
              </div>

              {/* Habilidades */}
              <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                  Habilidades
                </h2>
                {skillsData && skillsData.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '0.75rem',
                    marginTop: '0.5rem'
                  }}>
                    {skillsData.map(skill => (
                      <label
                        key={skill.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem',
                          border: formData.skill_ids?.includes(skill.id)
                            ? '2px solid var(--primary)'
                            : '1px solid var(--border)',
                          borderRadius: 'var(--radius-lg)',
                          cursor: 'pointer',
                          background: formData.skill_ids?.includes(skill.id)
                            ? 'var(--primary-light)'
                            : 'var(--surface)',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!formData.skill_ids?.includes(skill.id)) {
                            e.currentTarget.style.borderColor = 'var(--primary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!formData.skill_ids?.includes(skill.id)) {
                            e.currentTarget.style.borderColor = 'var(--border)';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.skill_ids?.includes(skill.id) || false}
                          onChange={() => handleSkillToggle(skill.id)}
                          style={{ cursor: 'pointer' }}
                        />
                        {skill.icon && (
                          <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>
                            {skill.icon}
                          </span>
                        )}
                        <span style={{ flex: 1, fontWeight: formData.skill_ids?.includes(skill.id) ? '500' : '400' }}>
                          {skill.name}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '1rem',
                    background: 'var(--surface-hover)',
                    borderRadius: 'var(--radius-lg)',
                    color: 'var(--text-muted)',
                    fontSize: '0.875rem',
                    textAlign: 'center'
                  }}>
                    Nenhuma habilidade disponível. As habilidades serão criadas durante o onboarding.
                  </div>
                )}
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  Selecione as habilidades que este membro possui
                </p>
              </div>
            </>
          )}

          {/* Botões de Ação */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/members')}
              disabled={memberMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={memberMutation.isPending}
            >
              {memberMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  Salvando...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">save</span>
                  {isEdit ? 'Salvar Alterações' : 'Criar Membro'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

export default MemberFormPage;

